<template>
    <div>
        <h1>Register</h1>
        <p class="text-surface-500 dark:text-surface-400 block mb-8">Please register with the form below</p>
        <Button type="button" label="Back to Login" @click="showRegister = false"></Button>
        <Divider />
        <div class="mb-8">
            <h2>Username:</h2>
            <InputText v-model="username" placeholder="Username" class="wide"/>
            <h2>Email:</h2>
            <InputText v-model="email" placeholder="Email" />
            <h2>Password:</h2>
            <InputText v-model="password" placeholder="Password" type="password" class="wide"/>
            <h2>Re-enter Password:</h2>
            <InputText v-model="passwordCheck" placeholder="Password" type="password" class="wide"/>
            <Divider />
            <h2>Data Usage Consent</h2>
            <div class="flex-row items-center gap-1">
                <p>
                <!-- For terms and conditions, see: <a @click="router.push('/gdpr')">GDPR</a> page. -->
                    For terms and conditions see our <a href="/gdpr">data policy</a>.
                </p>
                <p>
                Please confirm you are happy for data you enter to be stored in our database, and for session cookies to be stored on your browser.
                </p>
                <Checkbox v-model="gdprAck" label="I acknowledge stuff" binary />
            </div>
            <div class="flex-row items-center gap-1">
                Please accept or refuse optional cookies used for QA and performance monitoring.
                <Checkbox v-model="qaCookies"  binary />
            </div>
        </div>
        <div class="flex items-center mb-8 gap-1">
            <Button type="button" label="Register" @click="register_submit"></Button>
        </div>
        <p class="error" v-if="showInvalidRegisterMessage">{{ registrationErrorMessage }}</p>
        <p class="error" v-if="showMissingFieldsMessage">Please fill in all fields</p>
        <p class="error" v-if="showPasswordMismatchMessage">Passwords do not match</p>
        <p class="error" v-if="showRegisterError">Error registering</p>
        <p class="success" v-if="showRegistrationSuccess">{{ registrationSuccessMessage }}</p>
        <p class="error" v-if="showGDPRMessage">Please check the box above to provide consent to our <a href="/gdpr">data policy</a>, which will allow the app to store your registration.</p>
    </div>
</template>

<script setup lang="ts">

import { useAuthStore } from '@/stores/auth'
import { ref } from 'vue'

const authStore = useAuthStore()
const showInvalidRegisterMessage = ref(false)
const showMissingFieldsMessage = ref(false)
const showPasswordMismatchMessage = ref(false)
const showRegisterError = ref(false)
const showGDPRMessage = ref(false)
const showRegistrationSuccess = ref(false)
const registrationErrorMessage = ref('')
const registrationSuccessMessage = ref('')

const showRegister = defineModel<boolean>('showRegister')
const closeDialogPlease = defineModel<boolean>('closeDialogPlease')

const username = ref('')
const password = ref('')
const passwordCheck = ref('')
const email = ref('')
const gdprAck = ref(false)
const qaCookies = ref(false)

const register_submit = async () => {
    // Reset all messages
    showInvalidRegisterMessage.value = false
    showMissingFieldsMessage.value = false
    showPasswordMismatchMessage.value = false
    showRegisterError.value = false
    showGDPRMessage.value = false
    showRegistrationSuccess.value = false
    registrationErrorMessage.value = ''
    registrationSuccessMessage.value = ''
    
    // Validation
    if (username.value === '' || password.value === '' || email.value === '') {
        showMissingFieldsMessage.value = true
        return;
    } 
    
    if (password.value !== passwordCheck.value) {
        showPasswordMismatchMessage.value = true
        return;
    }
    
    if (!gdprAck.value) {
        showGDPRMessage.value = true
        return;
    }

    try {
        const SIGNUP_URL = '/api/signup/'
        
        // Get CSRF token if needed
        const csrfToken = await authStore.getCsrfToken()
        const headers: HeadersInit = {
            'Content-Type': 'application/json'
        }
        
        if (csrfToken) {
            headers['X-CSRFToken'] = csrfToken
        }

        const response = await fetch(SIGNUP_URL, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({
                username: username.value,
                password: password.value,
                email: email.value
            })
        })

        const data = await response.json()

        if (response.ok && data.success) {
            // Registration successful - show success message
            showRegistrationSuccess.value = true
            registrationSuccessMessage.value = data.message || 'Registration successful! Your account is pending admin approval.'
            
            // Clear form
            username.value = ''
            password.value = ''
            passwordCheck.value = ''
            email.value = ''
            
            // Close the registration form after a delay
            setTimeout(() => {
                showRegister.value = false
                closeDialogPlease.value = true
            }, 3000)
            
        } else {
            // Registration failed - show error
            showInvalidRegisterMessage.value = true
            registrationErrorMessage.value = data.error || 'Registration failed. Please try again.'
        }
        
    } catch (error) {
        console.error('Registration error:', error)
        showRegisterError.value = true
        registrationErrorMessage.value = 'Network error. Please check your connection and try again.'
    }
}

</script>

<style scoped>
.error {
    /* Use style built in to primevue */
    color: var(--p-button-text-danger-color);
}

.success {
    /* Use primevue success color */
    color: #000000;
    background: #cdeebb;
    padding: 0.75rem 1rem;
    border-radius: 0.375rem;
    border: 1px solid var(--p-button-success-border-color);
    margin: 0.5rem 0;
}

.wide {
    width: 100%;
}
</style>